/*! @wharfkit/protocol-esr v4.0.2 | BSD-3-Clause | Copyright (c) 2023 Greymass Inc. | https://github.com/wharfkit/js */
import { ChainId, PrivateKey, ResolvedSigningRequest, Signature, SignedTransaction, SigningRequest, Struct } from "@wharfkit/session";
import { receive } from "@greymass/buoy";
import { sealedMessagePayload } from "@wharfkit/sealed-messages";
import * as zlib from "pako";
//#region \0@oxc-project+runtime@0.147.0/helpers/esm/decorate.js
function __decorate(decorators, target, key, desc) {
	var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
	if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
	else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
	return c > 3 && r && Object.defineProperty(target, key, r), r;
}
//#endregion
//#region src/anchor-types.ts
let LinkCreate = class LinkCreate extends Struct {};
__decorate([Struct.field("name")], LinkCreate.prototype, "session_name", void 0);
__decorate([Struct.field("public_key")], LinkCreate.prototype, "request_key", void 0);
__decorate([Struct.field("string", { extension: true })], LinkCreate.prototype, "user_agent", void 0);
LinkCreate = __decorate([Struct.type("link_create")], LinkCreate);
let LinkInfo = class LinkInfo extends Struct {};
__decorate([Struct.field("time_point_sec")], LinkInfo.prototype, "expiration", void 0);
LinkInfo = __decorate([Struct.type("link_info")], LinkInfo);
//#endregion
//#region src/buoy-types.ts
let BuoyMessage = class BuoyMessage extends Struct {};
__decorate([Struct.field("public_key")], BuoyMessage.prototype, "from", void 0);
__decorate([Struct.field("uint64")], BuoyMessage.prototype, "nonce", void 0);
__decorate([Struct.field("bytes")], BuoyMessage.prototype, "ciphertext", void 0);
__decorate([Struct.field("uint32")], BuoyMessage.prototype, "checksum", void 0);
BuoyMessage = __decorate([Struct.type("buoy_message")], BuoyMessage);
let BuoySession = class BuoySession extends Struct {};
__decorate([Struct.field("name")], BuoySession.prototype, "session_name", void 0);
__decorate([Struct.field("public_key")], BuoySession.prototype, "request_key", void 0);
__decorate([Struct.field("string", { extension: true })], BuoySession.prototype, "user_agent", void 0);
BuoySession = __decorate([Struct.type("buoy_session")], BuoySession);
let BuoyInfo = class BuoyInfo extends Struct {};
__decorate([Struct.field("time_point_sec")], BuoyInfo.prototype, "expiration", void 0);
BuoyInfo = __decorate([Struct.type("buoy_info")], BuoyInfo);
//#endregion
//#region src/callback.ts
async function waitForCallback(callbackArgs, buoyWs, t) {
	const callbackResponse = await receive({
		...callbackArgs,
		WebSocket: buoyWs || WebSocket
	});
	if (!callbackResponse) throw new Error(callbackResponse.rejected);
	if (typeof callbackResponse.rejected === "string") throw new Error(callbackResponse.rejected);
	const payload = JSON.parse(callbackResponse);
	if (payload.sa === void 0 || payload.sp === void 0 || payload.cid === void 0) throw new Error(t("error.cancelled", { default: "The request was cancelled from Anchor." }));
	return payload;
}
//#endregion
//#region src/utils.ts
function snakeToPascal(name) {
	return name.split("_").map((v) => (v[0] ? v[0].toUpperCase() : "") + v.slice(1)).join("");
}
function snakeToCamel(name) {
	const pascal = snakeToPascal(name);
	return (pascal[0] ? pascal[0].toLowerCase() : "") + pascal.slice(1);
}
function logWarn(...args) {
	console.warn("[anchor-link]", ...args);
}
function uuid() {
	let uuid = "", ii;
	const chars = "0123456789abcdef";
	for (ii = 0; ii < 36; ii += 1) switch (ii) {
		case 8:
		case 13:
		case 18:
		case 23:
			uuid += "-";
			break;
		case 14:
			uuid += "4";
			break;
		case 19:
			uuid += chars[Math.random() * 4 | 8];
			break;
		default: uuid += chars[Math.random() * 16 | 0];
	}
	return uuid;
}
function generateReturnUrl() {
	if (isAppleHandheld() && isReactNativeApp()) return;
	if (isChromeiOS()) return "googlechrome://";
	if (isFirefoxiOS()) return "firefox:://";
	if (isAppleHandheld() && isBrave()) return "brave://";
	if (isAppleHandheld()) {
		const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
		let rv = window.location.href.split("#")[0] + "#";
		for (let i = 0; i < 8; i++) rv += alphabet.charAt(Math.floor(Math.random() * 62));
		return rv;
	}
	if (isAndroid() && isFirefox()) return "android-app://org.mozilla.firefox";
	if (isAndroid() && isEdge()) return "android-app://com.microsoft.emmx";
	if (isAndroid() && isOpera()) return "android-app://com.opera.browser";
	if (isAndroid() && isBrave()) return "android-app://com.brave.browser";
	if (isAndroid() && isAndroidWebView()) return "android-app://webview";
	if (isAndroid() && isChromeMobile()) return "android-app://com.android.chrome";
	return window.location.href;
}
function isAppleHandheld() {
	return /iP(ad|od|hone)/i.test(navigator.userAgent);
}
function isChromeiOS() {
	return /CriOS/.test(navigator.userAgent);
}
function isChromeMobile() {
	return /Chrome\/[.0-9]* Mobile/i.test(navigator.userAgent);
}
function isFirefox() {
	return /Firefox/i.test(navigator.userAgent);
}
function isFirefoxiOS() {
	return /FxiOS/.test(navigator.userAgent);
}
function isOpera() {
	return /OPR/.test(navigator.userAgent) || /Opera/.test(navigator.userAgent);
}
function isEdge() {
	return /Edg/.test(navigator.userAgent);
}
function isBrave() {
	return navigator["brave"] && typeof navigator["brave"].isBrave === "function";
}
function isAndroid() {
	return /Android/.test(navigator.userAgent);
}
function isReactNativeApp() {
	return !!window.ReactNativeWebView;
}
function isAndroidWebView() {
	return /wv/.test(navigator.userAgent) || /Android/.test(navigator.userAgent) && isReactNativeApp();
}
function isKnownMobile() {
	return isAppleHandheld() || isChromeiOS() || isChromeMobile() || isFirefoxiOS() || isAndroid() || isAndroidWebView();
}
//#endregion
//#region src/esr.ts
async function createIdentityRequest(context, buoyUrl) {
	const privateKey = PrivateKey.generate("K1");
	const requestKey = privateKey.toPublic();
	const createInfo = BuoySession.from({
		session_name: context.appName,
		request_key: requestKey,
		user_agent: getUserAgent()
	});
	const isMultiChain = !(context.chain || context.chains.length === 1);
	const callbackChannel = prepareCallbackChannel(buoyUrl);
	const chainId = isMultiChain ? null : context.chain ? ChainId.from(context.chain.id.array) : null;
	const chainIds = isMultiChain ? context.chains.map((c) => ChainId.from(c.id.array)) : [];
	const request = SigningRequest.identity({
		callback: prepareCallback(callbackChannel),
		scope: String(context.appName),
		chainId,
		chainIds,
		info: {
			link: createInfo,
			scope: String(context.appName)
		}
	}, context.esrOptions);
	const sameDeviceRequest = request.clone();
	if (typeof window !== "undefined") {
		const returnUrl = generateReturnUrl();
		sameDeviceRequest.setInfoKey("same_device", true);
		if (returnUrl !== void 0) sameDeviceRequest.setInfoKey("return_path", returnUrl);
	}
	return {
		callback: callbackChannel,
		request,
		sameDeviceRequest,
		requestKey,
		privateKey
	};
}
function setTransactionCallback(request, buoyUrl) {
	const callback = prepareCallbackChannel(buoyUrl);
	request.setCallback(`${callback.service}/${callback.channel}`, true);
	return callback;
}
function getUserAgent() {
	let agent = `@wharfkit/protocol-esr __ver`;
	if (typeof navigator !== "undefined") agent += " " + navigator.userAgent;
	return agent;
}
function prepareCallback(callbackChannel) {
	const { service, channel } = callbackChannel;
	return {
		url: `${service}/${channel}`,
		background: true
	};
}
function prepareCallbackChannel(buoyUrl) {
	return {
		service: buoyUrl,
		channel: uuid()
	};
}
async function sealMessage(message, privateKey, publicKey, nonce) {
	return sealedMessagePayload(message, privateKey, publicKey, nonce);
}
async function verifyLoginCallbackResponse(callbackResponse, context) {
	if (!callbackResponse.sig || callbackResponse.sig.length === 0) throw new Error("Invalid response, must have at least one signature");
	let chain;
	if (!context.chain && context.chains.length > 1) {
		if (!callbackResponse.cid) throw new Error("Multi chain response payload must specify resolved chain id (cid)");
	} else {
		chain = context.chain || context.chains[0];
		if (callbackResponse.cid && String(chain.id) !== callbackResponse.cid) throw new Error("Got response for wrong chain id");
	}
}
function extractSignaturesFromCallback(payload) {
	const signatures = [];
	if (payload.sig) signatures.push(String(payload.sig));
	const indexed = Object.keys(payload).map((key) => /^sig(\d+)$/.exec(key)).filter((match) => match !== null).sort((a, b) => Number(a[1]) - Number(b[1]));
	for (const match of indexed) {
		const sig = payload[match[0]];
		if (sig) signatures.push(String(sig));
	}
	return [...new Set(signatures)].map((s) => Signature.from(s));
}
function isCallback(object) {
	return "tx" in object;
}
//#endregion
//#region src/handoff.ts
const TRANSACTION_HANDOFF_KEY = "wharfkit:anchor-transaction-handoff";
function isCallbackChannel(value) {
	if (!value || typeof value !== "object") return false;
	const candidate = value;
	return typeof candidate.channel === "string" && typeof candidate.service === "string";
}
function isTransactionHandoff(value) {
	if (!value || typeof value !== "object") return false;
	const candidate = value;
	return candidate.version === 1 && typeof candidate.returnUrl === "string" && isCallbackChannel(candidate.callback) && typeof candidate.transactionId === "string" && typeof candidate.chainId === "string" && typeof candidate.actor === "string" && typeof candidate.permission === "string" && typeof candidate.expiresAt === "string";
}
function isSamePageReturn(returnUrl, currentUrl = window.location.href) {
	const current = new URL(currentUrl);
	const target = new URL(returnUrl, current);
	const targetHash = target.hash;
	current.hash = "";
	target.hash = "";
	return targetHash.length > 1 && current.href === target.href;
}
function waitForPageReturn(returnUrl, signal, pageWindow = window, pageDocument = document) {
	const expectedHash = new URL(returnUrl, pageWindow.location.href).hash;
	let departed = pageDocument.visibilityState === "hidden";
	return new Promise((resolve, reject) => {
		const cleanup = () => {
			pageWindow.removeEventListener("hashchange", onHashChange);
			pageWindow.removeEventListener("pagehide", onPageHide);
			pageWindow.removeEventListener("pageshow", onPageShow);
			pageDocument.removeEventListener("visibilitychange", onVisibilityChange);
			signal?.removeEventListener("abort", onAbort);
		};
		const finish = () => {
			cleanup();
			resolve();
		};
		const onHashChange = () => {
			if (expectedHash && pageWindow.location.hash === expectedHash) finish();
		};
		const onPageHide = () => {
			departed = true;
		};
		const onPageShow = () => {
			if (departed) finish();
		};
		const onVisibilityChange = () => {
			if (pageDocument.visibilityState === "hidden") departed = true;
			else if (departed) finish();
		};
		const onAbort = () => {
			cleanup();
			reject(new Error("Transaction callback wait cancelled"));
		};
		pageWindow.addEventListener("hashchange", onHashChange);
		pageWindow.addEventListener("pagehide", onPageHide);
		pageWindow.addEventListener("pageshow", onPageShow);
		pageDocument.addEventListener("visibilitychange", onVisibilityChange);
		signal?.addEventListener("abort", onAbort, { once: true });
		if (signal?.aborted) onAbort();
	});
}
function storeTransactionHandoff(handoff, storage = window.localStorage) {
	storage.setItem(TRANSACTION_HANDOFF_KEY, JSON.stringify(handoff));
}
function findReturnedTransactionHandoff(currentUrl = window.location.href, now = Date.now(), storage = window.localStorage) {
	const encoded = storage.getItem(TRANSACTION_HANDOFF_KEY);
	if (!encoded) return null;
	let handoff;
	try {
		handoff = JSON.parse(encoded);
	} catch {
		storage.removeItem(TRANSACTION_HANDOFF_KEY);
		return null;
	}
	if (!isTransactionHandoff(handoff)) {
		storage.removeItem(TRANSACTION_HANDOFF_KEY);
		return null;
	}
	const expiresAt = Date.parse(handoff.expiresAt);
	if (!Number.isFinite(expiresAt) || expiresAt <= now) {
		storage.removeItem(TRANSACTION_HANDOFF_KEY);
		return null;
	}
	try {
		return new URL(currentUrl).href === new URL(handoff.returnUrl).href ? handoff : null;
	} catch {
		storage.removeItem(TRANSACTION_HANDOFF_KEY);
		return null;
	}
}
function clearTransactionHandoff(handoff, storage = window.localStorage) {
	if (findReturnedTransactionHandoff(handoff.returnUrl, 0, storage)?.returnUrl === handoff.returnUrl) storage.removeItem(TRANSACTION_HANDOFF_KEY);
}
function receiveReturnedTransaction(options) {
	const storage = options.storage ?? window.localStorage;
	const now = options.now ?? Date.now();
	const handoff = findReturnedTransactionHandoff(options.currentUrl, now, storage);
	if (!handoff) return null;
	const ctx = {};
	const transaction = (options.receive ?? receive)({
		...handoff.callback,
		WebSocket: options.WebSocket ?? WebSocket,
		timeout: Math.max(1, Date.parse(handoff.expiresAt) - now)
	}, ctx).then(async (response) => {
		if (typeof response !== "string") throw new Error("Anchor did not return a signed transaction");
		const payload = JSON.parse(response);
		const signatures = extractSignaturesFromCallback(payload);
		if (!isCallback(payload) || signatures.length === 0) throw new Error("Anchor did not return a signed transaction");
		const resolved = await ResolvedSigningRequest.fromPayload(payload, {
			zlib,
			abiProvider: options.abiProvider
		});
		if (String(resolved.transaction.id) !== handoff.transactionId || String(resolved.chainId) !== handoff.chainId || String(resolved.signer.actor) !== handoff.actor || String(resolved.signer.permission) !== handoff.permission) throw new Error("Anchor returned a different transaction");
		return SignedTransaction.from({
			...resolved.transaction,
			signatures
		});
	}).finally(() => clearTransactionHandoff(handoff, storage));
	transaction.cancel = () => {
		ctx.cancel?.();
		return transaction;
	};
	return transaction;
}
//#endregion
export { BuoyInfo, BuoyMessage, BuoySession, LinkCreate, LinkInfo, clearTransactionHandoff, createIdentityRequest, extractSignaturesFromCallback, findReturnedTransactionHandoff, generateReturnUrl, getUserAgent, isAndroid, isAndroidWebView, isAppleHandheld, isBrave, isCallback, isChromeMobile, isChromeiOS, isEdge, isFirefox, isFirefoxiOS, isKnownMobile, isOpera, isReactNativeApp, isSamePageReturn, logWarn, prepareCallback, receiveReturnedTransaction, sealMessage, setTransactionCallback, snakeToCamel, snakeToPascal, storeTransactionHandoff, uuid, verifyLoginCallbackResponse, waitForCallback, waitForPageReturn };

//# sourceMappingURL=protocol-esr.m.js.map